'use client';

import { useState, useEffect, useMemo } from 'react';
import { useSession } from 'next-auth/react';

/**
 * Hook to fetch and manage trade pricing state.
 * Handles all loading states properly to prevent flash of content.
 *
 * @param {Object} params
 * @param {number|null} params.subProductId - Product ID to fetch pricing for
 * @param {Array} params.options - Selected product options (for price calculation)
 * @param {Array} params.swatches - Selected swatches (for price calculation)
 * @returns {Object} { loading, isSignedIn, isTradeAccount, tradePricing }
 */
export function useTradePricing({
  subProductId = null,
  options = null,
  swatches = null
} = {}) {
  const {
    status
  } = useSession();
  const [tradePricing, setTradePricing] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Derived states
  const sessionLoading = status === 'loading';
  const isSignedIn = status === 'authenticated';

  // Stable keys for options/swatches to use in dependency array
  const optionsKey = useMemo(() => options ? JSON.stringify(options) : null, [options]);
  const swatchesKey = useMemo(() => swatches ? JSON.stringify(swatches) : null, [swatches]);
  useEffect(() => {
    // Session still loading - wait
    if (sessionLoading) {
      return;
    }

    // Not signed in - nothing to fetch
    if (!isSignedIn) {
      setInitialLoadComplete(true);
      return;
    }

    // Signed in but no product ID - nothing to fetch
    if (!subProductId) {
      setInitialLoadComplete(true);
      return;
    }

    // Signed in with product ID - fetch trade pricing
    let cancelled = false;
    const fetchTradePricing = async () => {
      try {
        const response = await fetch('/api/product/trade-pricing', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sub_product_id: subProductId,
            options: options || [],
            swatches: swatches || []
          })
        });
        const data = await response.json();
        if (cancelled) return;
        if (data.success) {
          setTradePricing(data);
        }
      } catch (error) {
        console.error('Error fetching trade pricing:', error);
      } finally {
        if (!cancelled) {
          setInitialLoadComplete(true);
        }
      }
    };
    fetchTradePricing();
    return () => {
      cancelled = true;
    };
  }, [subProductId, isSignedIn, sessionLoading, optionsKey, swatchesKey]);

  // Loading is true only during initial load (session loading or first fetch)
  const loading = sessionLoading || !initialLoadComplete;
  return {
    loading,
    isSignedIn,
    isTradeAccount: !!tradePricing?.trade_account,
    tradePricing
  };
}
export default useTradePricing;